import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateField, addGalleryPhoto, removeGalleryPhoto } from "../../../reduxSlice/profileSlice";
import { postApiWithAuth } from "../../../services/api/api";
import * as faceapi from "@vladmandic/face-api";

export const validateStep5 = (data) => {
    const errors = {};
    if (!data.mainPhoto) {
        errors.mainPhoto = "A primary profile picture is mandatory";
    }
    if (data.mainPhotoFileError) errors.mainPhoto = data.mainPhotoFileError;
    return errors;
};

const Step5ProfileMedia = ({ setIsMediaProcessing }) => {
    const dispatch = useDispatch();
    const errors = useSelector((state) => state.profile.errors || {});
    const { mainPhoto, galleryPhotos = [], videoFile, id: profileId } = useSelector((state) => state.profile);

    const [mainError, setMainError] = useState("");
    const [galleryError, setGalleryError] = useState("");
    const [videoError, setVideoError] = useState("");
    const [detectingFace, setDetectingFace] = useState(false);
    const [isModelLoaded, setIsModelLoaded] = useState(false);
    const [modelError, setModelError] = useState("");
    const [hoveredImage, setHoveredImage] = useState(null);

    const [deletingPhotoIndex, setDeletingPhotoIndex] = useState(null);
    const [isDeletingVideo, setIsDeletingVideo] = useState(false);

    const MAX_PHOTO_SIZE = 5 * 1024 * 1024;
    const MAX_VIDEO_SIZE = 20 * 1024 * 1024;

    // ALLOWED FILE FORMAT EXTENSIONS
    const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

    useEffect(() => {
        const loadModel = async () => {
            try {
                const modelFolderUrl = process.env.PUBLIC_URL + "/models";
                await faceapi.nets.tinyFaceDetector.loadFromUri(modelFolderUrl);
                await faceapi.nets.faceLandmark68Net.loadFromUri(modelFolderUrl);
                setIsModelLoaded(true);
                setModelError("");
            } catch (err) {
                console.error("Failed to load client-side face detection models:", err);
                setModelError("Face engine initialization failed. Check your network or path routing configs.");
            }
        };
        loadModel();
    }, []);

    useEffect(() => {
        if (typeof setIsMediaProcessing === "function") {
            setIsMediaProcessing(detectingFace || !isModelLoaded);
        }
    }, [detectingFace, isModelLoaded, setIsMediaProcessing]);

    useEffect(() => {
        const simulatedData = { mainPhoto, mainPhotoFileError: mainError };
        const generatedErrors = validateStep5(simulatedData);
        const nextErrorString = generatedErrors.mainPhoto || "";

        if (errors.mainPhoto !== nextErrorString) {
            dispatch(updateField({
                field: "errors",
                value: { ...errors, mainPhoto: nextErrorString }
            }));
        }
    }, [mainPhoto, mainError, dispatch, errors]);

    // Helper Utility to convert Files into highly compatible Base64 strings safely on mobile
    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleMainPhotoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setMainError("");
        dispatch(updateField({ field: "mainPhotoFileError", value: "" }));

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            const formatError = "Invalid file type. Please upload a clear image in JPG, JPEG, or PNG format.";
            setMainError(formatError);
            dispatch(updateField({ field: "mainPhotoFileError", value: formatError }));
            return;
        }

        if (file.size > MAX_PHOTO_SIZE) {
            const sizeErrMsg = `Photo must be a maximum of 5 MB.`;
            setMainError(sizeErrMsg);
            dispatch(updateField({ field: "mainPhotoFileError", value: sizeErrMsg }));
            return;
        }

        if (!isModelLoaded) {
            const loadingErrMsg = "Face detection modules are still initializing. Please wait a moment.";
            setMainError(loadingErrMsg);
            dispatch(updateField({ field: "mainPhotoFileError", value: loadingErrMsg }));
            return;
        }

        setDetectingFace(true);

        try {
            const base64Data = await convertToBase64(file);
            const img = new Image();
            img.src = base64Data;

            img.onload = async () => {
                try {
                    let detections = [];

                    // Pass 1: Fast scan optimized for high-res close ups (Mandar)
                    detections = await faceapi.detectAllFaces(
                        img,
                        new faceapi.TinyFaceDetectorOptions({ inputSize: 160, scoreThreshold: 0.45 })
                    ).withFaceLandmarks();

                    // Pass 2: Deeper resolution scan if Pass 1 misses (Deepak)
                    if (!detections || detections.length === 0) {
                        detections = await faceapi.detectAllFaces(
                            img,
                            new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.35 })
                        ).withFaceLandmarks();
                    }

                    if (!detections || detections.length === 0) {
                        const noFaceMsg = "No face detected. Please upload a clear profile headshot.";
                        setMainError(noFaceMsg);
                        dispatch(updateField({ field: "mainPhotoFileError", value: noFaceMsg }));
                        setDetectingFace(false);
                        return;
                    }

                    const face = detections[0];
                    const landmarks = face.landmarks;
                    const leftEye = landmarks.getLeftEye() || [];
                    const rightEye = landmarks.getRightEye() || [];
                    const nose = landmarks.getNose() || [];

                    if (leftEye.length > 0 && rightEye.length > 0 && nose.length > 3) {
                        // Calculate center point of both eyes
                        const leftEyeX = leftEye.reduce((sum, pt) => sum + pt.x, 0) / leftEye.length;
                        const rightEyeX = rightEye.reduce((sum, pt) => sum + pt.x, 0) / rightEye.length;
                        const noseTipX = nose[3].x;

                        const leftDistance = Math.abs(noseTipX - leftEyeX);
                        const rightDistance = Math.abs(rightEyeX - noseTipX);

                        // Normalize the total width to scale validation math dynamically across far/near crops
                        const totalEyeWidth = Math.abs(rightEyeX - leftEyeX);
                        const asymmetryDiff = Math.abs(leftDistance - rightDistance);

                        // 1. Structural Check: The difference between eye-to-nose distances
                        // should not exceed 32% of total eye span for front-facing photos.
                        const profileRatio = asymmetryDiff / totalEyeWidth;

                        // 2. Head Tilt Check: Detect roll angle adjustments
                        const rollAngle = face.angle?.roll || 0;
                        const maxAllowedRatio = Math.abs(rollAngle) > 15 ? 0.38 : 0.32;

                        if (profileRatio > maxAllowedRatio) {
                            const sidePhotoMsg = "Side profile photos are not allowed. Please upload a straight, front-facing photo.";
                            setMainError(sidePhotoMsg);
                            dispatch(updateField({ field: "mainPhotoFileError", value: sidePhotoMsg }));
                            setDetectingFace(false);
                            return;
                        }
                    }

                    dispatch(updateField({ field: "mainPhoto", value: base64Data }));
                } catch (err) {
                    console.error("Face calculation exception handler:", err);
                    setMainError("Image parsing failed. Please retry.");
                    dispatch(updateField({ field: "mainPhotoFileError", value: "Image parsing failed." }));
                } finally {
                    setDetectingFace(false);
                }
            };
        } catch (baseError) {
            console.error("Base64 breakdown:", baseError);
            setMainError("Failed to convert image media stream.");
            setDetectingFace(false);
        }
    };

    const handleGalleryPhotoChange = async (e) => {
        const files = Array.from(e.target.files);
        setGalleryError("");

        let invalidFilesFound = false;
        let nonImageFilesFound = false;

        for (const file of files) {
            // Validation check format filter
            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                nonImageFilesFound = true;
                continue;
            }

            if (file.size > MAX_PHOTO_SIZE) {
                invalidFilesFound = true;
                return;
            }

            try {
                const base64Url = await convertToBase64(file);
                dispatch(addGalleryPhoto({
                    id: null,
                    url: base64Url,
                    photo: base64Url
                }));
            } catch (err) {
                console.error("Gallery conversion failed code paths:", err);
            }
        }

        if (nonImageFilesFound) {
            setGalleryError("One or more files were skipped. Only standard image files (JPG, PNG, WEBP) are allowed.");
        } else if (invalidFilesFound) {
            setGalleryError("One or more photos were skipped. All gallery items must be a maximum of 5 MB.");
        }
    };

    const handleVideoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setVideoError("");

        if (file.size > MAX_VIDEO_SIZE) {
            setVideoError(`Video must be a maximum of 20 MB (Selected file size: ${(file.size / (1024 * 1024)).toFixed(2)} MB)`);
            return;
        }

        try {
            // Converts video payloads securely to base64 strings
            const base64Video = await convertToBase64(file);
            dispatch(updateField({ field: "videoFile", value: base64Video }));
        } catch (err) {
            console.error("Video conversion error:", err);
            setVideoError("Failed to encode video file framework on this mobile browser setup.");
        }
    };

    const handleDeleteGalleryPhoto = async (index, item) => {
        setHoveredImage(null);
        setGalleryError("");

        const imgUrl = item?.url || item?.photo || "";
        const isBlobOrBase64Local = imgUrl.startsWith("blob:") || imgUrl.startsWith("data:image");

        if (isBlobOrBase64Local || !item.id) {
            dispatch(removeGalleryPhoto(index));
            return;
        }

        try {
            setDeletingPhotoIndex(index);
            const response = await postApiWithAuth("/photo/delete", {
                profile_id: profileId,
                id: item.id
            });

            if (response?.status) {
                dispatch(removeGalleryPhoto(index));
            } else {
                setGalleryError(response?.message || "Failed to remove photo image from server storage.");
            }
        } catch (err) {
            console.error("Server image deletion encountered an issue:", err);
            setGalleryError("Connection error: Unable to complete removal transaction.");
        } finally {
            setDeletingPhotoIndex(null);
        }
    };

    const handleDeleteVideo = async () => {
        setVideoError("");

        if (videoFile && (videoFile.startsWith("blob:") || videoFile.startsWith("data:video"))) {
            dispatch(updateField({ field: "videoFile", value: null }));
            return;
        }

        try {
            setIsDeletingVideo(true);
            const response = await postApiWithAuth("/video/delete", {
                profile_id: profileId
            });

            if (response?.status) {
                dispatch(updateField({ field: "videoFile", value: null }));
            } else {
                setVideoError(response?.message || "Failed to remove profile video file from server storage.");
            }
        } catch (err) {
            console.error("Server video deletion encountered an issue:", err);
            setVideoError("Connection error: Unable to complete video removal transaction.");
        } finally {
            setIsDeletingVideo(false);
        }
    };

    const handleGalleryItemMouseEnter = (e, url) => {
        if (e.target.closest('.delete_btn')) {
            setHoveredImage(null);
            return;
        }
        setHoveredImage(url);
    };

    return (
        <div className="form-animate">
            <div className="row">
                {/* Main Profile Photo Section */}
                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Main Profile Photo<span className="m_feild">*</span></h5>
                        <input
                            type="file"
                            id="mainPhoto"
                            className="custom_file_input"
                            accept="image/jpeg, image/jpg, image/png"
                            onChange={handleMainPhotoChange}
                            disabled={detectingFace || !isModelLoaded}
                        />
                        <label
                            htmlFor="mainPhoto"
                            className={`custom_upload_box ${errors.mainPhoto ? "border-danger text-danger" : ""} ${!isModelLoaded ? "opacity-50" : ""}`}
                            style={{ cursor: (!isModelLoaded || detectingFace) ? "not-allowed" : "pointer" }}
                        >
                            <i className={`fa ${detectingFace ? "fa-spinner fa-spin" : (!isModelLoaded ? "fa-refresh fa-spin" : "fa-cloud-upload")}`}></i>
                            <div>
                                <h5>{detectingFace ? "Analyzing Position..." : (!isModelLoaded ? "Loading Tracking Engine..." : "Upload Main Photo")}</h5>
                                <p>File must be <b>max 5 MB</b> facing straight forward</p>
                            </div>
                        </label>
                        <span className="note">(Only main profile photo will be printed in Suchi book)</span>
                        {modelError && <div className="text-warning small mt-1">{modelError}</div>}
                        {errors.mainPhoto && <div className="text-danger small mt-1 fw-bold">{errors.mainPhoto}</div>}

                        {mainPhoto && !errors.mainPhoto && (
                            <div className="main-photo-inline-wrapper">
                                <div
                                    className="preview_box mt-3 profile-hover-trigger-wrap"
                                    onMouseEnter={() => setHoveredImage(mainPhoto)}
                                    onMouseLeave={() => setHoveredImage(null)}
                                >
                                    <img src={mainPhoto} alt="Main Profile" className="preview_img" />
                                    <div className="hover-search-badge"><i className="fa fa-search-plus"></i></div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Other Photos Section (Gallery) */}
                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Other Photos</h5>
                        <input
                            type="file"
                            id="galleryPhoto"
                            className="custom_file_input"
                            accept="image/jpeg, image/jpg, image/png"
                            multiple
                            onChange={handleGalleryPhotoChange}
                        />
                        <label htmlFor="galleryPhoto" className="custom_upload_box">
                            <i className="fa fa-image"></i>
                            <div>
                                <h5>Upload Gallery Photos</h5>
                                <p>Each photo must be a maximum of <b>5 MB</b></p>
                            </div>
                        </label>

                        {galleryError && <div className="text-danger small mt-1 fw-bold">{galleryError}</div>}

                        <div className={`preview_wrapper ${(galleryPhotos && galleryPhotos.length > 0) ? "show_preview" : ""}`}>
                            {galleryPhotos && galleryPhotos.map((item, index) => {
                                const imgUrl = typeof item === "string" ? item : (item?.url || item?.photo);
                                const isCurrentDeleting = deletingPhotoIndex === index;

                                if (!imgUrl) return null;

                                return (
                                    <div
                                        className="preview_box profile-hover-trigger-wrap"
                                        key={item?.id || index}
                                        onMouseEnter={(e) => handleGalleryItemMouseEnter(e, imgUrl)}
                                        onMouseMove={(e) => handleGalleryItemMouseEnter(e, imgUrl)}
                                        onMouseLeave={() => setHoveredImage(null)}
                                        style={{opacity: isCurrentDeleting ? 0.5 : 1}}
                                    >
                                        <img src={imgUrl} alt="Gallery Item" className="preview_img"/>
                                        <button
                                            type="button"
                                            className="delete_btn"
                                            disabled={isCurrentDeleting}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteGalleryPhoto(index, item);
                                            }}
                                        >
                                            {isCurrentDeleting ? <i className="fa fa-spinner fa-spin"></i> : "X"}
                                        </button>
                                        {!isCurrentDeleting &&
                                            <div className="hover-search-badge"><i className="fa fa-search-plus"></i></div>
                                        }
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Video Profile Section */}
                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Video Profile</h5>
                        <input
                            type="file"
                            id="profileVideo"
                            className="custom_file_input"
                            accept="video/*"
                            onChange={handleVideoChange}
                        />
                        <label htmlFor="profileVideo" className="custom_upload_box">
                            <i className="fa fa-video-camera"></i>
                            <div>
                                <h5>Upload Profile Video</h5>
                                <p>Video file must be a maximum of <b>20 MB</b></p>
                            </div>
                        </label>

                        {videoError && <div className="text-danger small mt-1 fw-bold">{videoError}</div>}
                        {videoFile && !videoError && (
                            <div className="video_preview mt-3 position-relative d-inline-block">
                                <video width="150" height="100" controls className="video_style">
                                    <source src={videoFile}/>
                                </video>
                                <button
                                    type="button"
                                    className="delete_btn"
                                    style={{top: "-10px", right: "-10px", position: "absolute"}}
                                    disabled={isDeletingVideo}
                                    onClick={handleDeleteVideo}
                                >
                                    {isDeletingVideo ? <i className="fa fa-spinner fa-spin"></i> : "X"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {hoveredImage && (
                <div className="hover-large-view-portal">
                    <div className="large-preview-card-frame">
                        <img src={hoveredImage} alt="Large Profile View Portfolio" className="large-fluid-img" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Step5ProfileMedia;